import { useState, useCallback } from 'react'
import { api } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { showErrorToast, showSuccessToast } from '../utils/toast'

export function useComments() {
  const [loading, setLoading] = useState(false)

  // Créer un commentaire
  const createComment = useCallback(async (courseId, content, parentId = null) => {
    setLoading(true)
    try {
      const data = await api.post(endpoints.createComment, {
        courseId,
        content,
        ...(parentId && { parentId })
      })
      showSuccessToast({ message: 'Commentaire ajouté avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de l\'ajout du commentaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer les commentaires
  const getComments = useCallback(async (courseId, options = {}) => {
    const { parentId, sortBy = 'recent', page = 1, limit = 20 } = options
    setLoading(true)
    try {
      const params = new URLSearchParams({
        courseId,
        sortBy,
        page: page.toString(),
        limit: limit.toString()
      })
      if (parentId) {
        params.append('parentId', parentId)
      }
      const data = await api.get(`${endpoints.getComments}?${params.toString()}`, {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement des commentaires'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer un commentaire spécifique
  const getComment = useCallback(async (commentId) => {
    setLoading(true)
    try {
      const data = await api.get(endpoints.getComment(commentId), {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement du commentaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Modifier un commentaire
  const updateComment = useCallback(async (commentId, content) => {
    setLoading(true)
    try {
      const data = await api.put(endpoints.updateComment(commentId), { content })
      showSuccessToast({ message: 'Commentaire modifié avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de la modification du commentaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Supprimer un commentaire
  const deleteComment = useCallback(async (commentId) => {
    setLoading(true)
    try {
      await api.delete(endpoints.deleteComment(commentId))
      showSuccessToast({ message: 'Commentaire supprimé avec succès' })
    } catch (error) {
      const message = error?.message || 'Erreur lors de la suppression du commentaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Liker un commentaire
  const likeComment = useCallback(async (commentId) => {
    setLoading(true)
    try {
      await api.post(endpoints.likeComment(commentId))
    } catch (error) {
      // Ne pas afficher d'erreur si déjà liké
      if (error?.status !== 400) {
        const message = error?.message || 'Erreur lors du like'
        showErrorToast({ message })
      }
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Unliker un commentaire
  const unlikeComment = useCallback(async (commentId) => {
    setLoading(true)
    try {
      await api.delete(endpoints.unlikeComment(commentId))
    } catch (error) {
      // Ne pas afficher d'erreur si pas liké
      if (error?.status !== 400) {
        const message = error?.message || 'Erreur lors de l\'unlike'
        showErrorToast({ message })
      }
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Épingler/Désépingler un commentaire
  const togglePinComment = useCallback(async (commentId) => {
    setLoading(true)
    try {
      const data = await api.post(endpoints.pinComment(commentId))
      showSuccessToast({ message: 'Commentaire épinglé/désépinglé avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de l\'épinglage du commentaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer les statistiques
  const getCommentStats = useCallback(async (courseId) => {
    setLoading(true)
    try {
      const data = await api.get(endpoints.getCommentStats(courseId), {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      // Ne pas afficher d'erreur pour les stats
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    createComment,
    getComments,
    getComment,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment,
    togglePinComment,
    getCommentStats
  }
}

